/**
 * ADR-0143 §8 (Option A) — Study detail.
 *
 * `/app/studies/:slug` — the browsable record: narrative, findings, artifacts,
 * fixtures, notes. Read-only content; the console's two writes touch no tree
 * file — the scope gesture (shared ScopeHeader → registry row) and the
 * document upload (store bytes, immutable paths).
 */

import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { InitiativeLinks } from "./TenantInitiatives.js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiFetch, apiFetchMultipart, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { TrailLinks, useWalkedTrail } from "../components/ActivityTrail.js";
import { LoadingState, ErrorState } from "../components/States.js";
import { ScopeHeader } from "../components/ScopeHeader.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { LandDocumentDialog, type LandTarget } from "../components/LandDocumentDialog.js";
import { landerLabel } from "../lib/documentSeries.js";
import { useMyGroups, type ContentScope } from "../lib/scope.js";
import { canRemirror, mirrorPill, type MirrorArtifactStatus } from "../lib/mirrorDisplay.js";
import { formatTimestamp } from "../lib/formatTime.js";
import { usageProse, type EntryUsage } from "../lib/knowledgeUsage.js";

type ScopeRule = { statement: string; authority: string; enforced: boolean };

/**
 * ADR-0181 — one version of a landed document.
 *
 * A version with `purged_at` set is a TOMBSTONE: its file is gone, but its
 * hash, size and attribution are kept permanently so a citation of it still
 * resolves. It renders as a row with no download link, never as a missing row.
 */
type DocumentVersion = {
  version_no: number;
  path: string;
  content_hash: string;
  bytes: number;
  landed_at: string;
  landed_by: string;
  pinned?: boolean;
  pinned_at?: string;
  pinned_by?: string;
  purged_at?: string;
  purged_by?: string;
  purge_reason?: string;
};

type Artifact = {
  kind: "operation" | "knowledge_entry" | "study" | "document" | "other";
  ref?: string;
  operation_id?: string;
  location?: string;
  content_hash?: string;
  bytes?: number;
  landed_at?: string;
  note?: string;
  added_at: string;
  warning?: string;
  /** ADR-0155 S5 decoration — present on landed documents when a sync policy covers this study. */
  mirror?: MirrorArtifactStatus;
  /** ADR-0181 — a landed document is a SERIES. Absent on external `location` references. */
  series_key?: string;
  /** The series' display name. This, never the key, is what a person sees. */
  name?: string;
  /** Newest first, as the hub projects it. */
  versions?: DocumentVersion[];
  /** DERIVED by the hub — the highest un-purged version, or null when all are purged. */
  current_version_no?: number | null;
  version_count?: number;
  purged_count?: number;
};

/** ADR-0181 — what a purge would reclaim. Empty `series` ⇒ the card renders nothing. */
type RetentionInfo = {
  tenant_bytes: number;
  quota_bytes: number;
  keep_superseded: number;
  series: Array<{ series_key: string; name: string; eligible_versions: number; reclaims_bytes: number }>;
};
type Fixture = {
  name: string;
  operation: string;
  output: string;
  recorded_at: string;
  last_check: { result: "pass" | "pass_with_drift" | "fail"; notes: string; checked_at: string; checked_by: string } | null;
  stale: boolean;
};
type FindingIndexRow = {
  number: number;
  kind: string;
  title: string;
  date: string;
  provenance: string;
  status: string;
  supersedes: number[];
  /**
   * ADR-0179 — null when the finding was never redacted. Redaction removes
   * text that must not remain readable and marks the finding permanently, so
   * a reader can always tell the record was changed rather than silently
   * differing from what it once said. Read-only here: the console shows the
   * state and offers no redact control — removal is a door gesture under its
   * own grant.
   */
  redacted_at: string | null;
  /** Attribution as the trees store it (`account:Jane Doe`) — rendered as the name alone. */
  redacted_by: string | null;
  redaction_reason: string | null;
};
type NoteIndexRow = { name: string; title: string; updated_at: string };

/**
 * ADR-0157 §6 — a captured query, as the hub's capture read surface serves it.
 * Read-only in v1: curation (promote / discard) is a door-led gesture.
 */
type CapturedQuery = {
  id: string;
  kind: "query" | "analysis";
  sql: string;
  connector_name: string | null;
  captured_at: string;
  result_status: string | null;
  result_row_count: number | null;
  disposition: "captured" | "promoted" | "discarded";
  discard_reason: string | null;
  /** ADR-0179 — null when never redacted. The SQL is replaced, never deleted. */
  redacted_at: string | null;
  redaction_reason: string | null;
  promoted_operation_name: string | null;
  promoted_node_label: string | null;
  call_count: number;
};
type CaptureAccounting = {
  operation: string | null;
  total: number;
  uncurated: number;
  promoted: number;
  discarded: number;
  last_captured_at: string | null;
};

/**
 * ADR-0158 §3.3/§5 — the server's reading of the record's condition. Computed
 * once in the hub and served identically to the door staple and this page; the
 * console renders it and never re-derives it. Optional: when the computation
 * degrades, the page simply shows no signal chrome.
 */
type RecordStateSignal = { code: string; message: string };
type RecordState = {
  healthy: boolean;
  sections: Array<{ key: string; heading: string; state: "present" | "empty" | "template" | "missing" }>;
  signals: RecordStateSignal[];
  counts: {
    findings: number;
    findings_since_narrative_update: number | null;
    open_items: number;
    unlanded_references: number;
    provenance_warns: number;
    /** ADR-0179 — informational only; it never affects `healthy` and gets no card pill. */
    redacted_findings: number;
    uncurated_captures?: number;
    stale_fixtures: number;
    unenforced_scope_rules: number;
    dangling_artifacts: number;
  };
};

/** ADR-0189 — who reads this study's products (derived server-side, never stored). */
type StudyConsumption = {
  consumers: {
    operations: Array<{
      id: string;
      name: string;
      via_datasets: Array<{ id: string; name: string }>;
      studies: Array<{ study_id: string; slug?: string; title?: string }>;
    }>;
    pages: Array<{ id: string; name: string; status?: string; via_operation: string }>;
    studies: Array<{
      study_id: string;
      slug?: string;
      title?: string;
      edges: Array<"references_study" | "attached_operation" | "reads_product">;
    }>;
    knowledge_entries: Array<{ ref: string; read_count: number; served_count: number; last_read_at: string | null }>;
  };
  machinery: {
    schedules: Array<{ id: string; name: string; schedule_status?: string; via_operation: string }>;
    watches: Array<{ name: string; via_operation: string }>;
  };
  summary: {
    consumer_count: number;
    counted: { operations: number; pages: number; studies: number; knowledge_entries: number };
  };
};

type StudyDetail = {
  /** ADR-0220 §3.5: the initiatives this study serves (the initiative's link table). */
  initiatives?: Array<{ id: string; ref?: string; name: string }>;
  study: {
    id: string;
    title: string;
    class: string;
    type: "instrument" | "outcome" | null;
    parent: string | null;
    status: string;
    scope: ContentScope;
    objective: string;
    resume_state: string | null;
    superseded_by: string | null;
    tags: string[];
    created_at: string;
    /** Who made the study. Survives scope changes; null when unrecorded. */
    created_by: string | null;
    updated_at: string;
  };
  slug: string;
  /**
   * Set when this study does not own its scope — scope belongs to the major
   * study and every study under it follows. Null on a major.
   */
  scope_inherited_from: { id: string; title: string; slug: string } | null;
  scope_block: {
    id: string;
    scope: ContentScope;
    owner_account_id: string | null;
    owner_group_id: string | null;
    owner_group_name: string | null;
  };
  /** null = the rollup could not be resolved right now — never rendered as "nothing consumes this". */
  consumption: StudyConsumption | null;
  /** Door retrieval counters (ADR-0186 Amendment 1); null = usage unavailable. */
  usage?: EntryUsage | null;
  scope_rules: ScopeRule[];
  artifacts: Artifact[];
  fixtures: Fixture[];
  findings_index: FindingIndexRow[];
  notes: NoteIndexRow[];
  record_state?: RecordState;
  /**
   * The landing gate's live window (null = closed). While open, a connected
   * agent may land documents / attach artifacts to this study through the
   * MCP door; the agent cannot open it itself — only this page can.
   */
  landing_approval: {
    approved_by: string;
    approved_at: string;
    expires_at: string;
    use_count: number;
    last_used_at: string | null;
  } | null;
  body: string;
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** ADR-0157 §6 — outcome as a pill plus words; "Unknown" is honest, never blank. */
function captureOutcome(c: CapturedQuery): JSX.Element {
  if (c.result_status === "succeeded") {
    return (
      <span>
        <span className="pill pillOk">Succeeded</span>
        {c.result_row_count != null ? (
          <span className="muted"> · {c.result_row_count.toLocaleString()} rows</span>
        ) : null}
      </span>
    );
  }
  if (c.result_status === "failed") return <span className="pill pillBad">Failed</span>;
  if (c.result_status === "cancelled") return <span className="pill pillNeutral">Cancelled</span>;
  return <span className="pill pillNeutral">Unknown</span>;
}

/**
 * ADR-0158 §5 — the signals the Overview card carries as plain muted lines
 * (they have no card of their own, and pill-crowding the status row would
 * bury them). Every other signal renders beside its owning card.
 */
const OVERVIEW_SIGNALS = ["open_items_at_close", "open_items_placeholder", "resume_anchor_stale", "scope_rules_unenforced"];

/**
 * Attribution as the trees store it is `account:Jane Doe` / `key:Some Agent`.
 * Surfaces show the name, never the prefix. Same prefix set the platform
 * knowledge screens strip (AdminPlatformKnowledge.tsx) — matched deliberately
 * so one attribution does not read two ways on two screens.
 */
function actorLabel(attribution: string | null): string {
  if (!attribution) return "";
  return attribution.replace(/^(account|key|capture|escalation|system):/, "");
}

/** A card-header pill: colour always paired with the words that carry the meaning. */
function signalPill(label: string): JSX.Element {
  return <span className="pill pillWarn">{label}</span>;
}

function checkPill(f: Fixture): JSX.Element {
  if (!f.last_check) return <span className="pill pillNeutral">Never checked</span>;
  switch (f.last_check.result) {
    case "pass":
      return <span className="pill pillOk">Pass</span>;
    case "pass_with_drift":
      return <span className="pill pillWarn">Pass with drift</span>;
    case "fail":
      return <span className="pill pillBad">Fail</span>;
    default:
      return <span className="pill">{f.last_check.result}</span>;
  }
}

export function TenantStudyDetail() {
  const { slug = "" } = useParams<{ slug: string }>();
  // ADR-0214 C7: opened from the Activity trail, the trail sits above the page's own breadcrumb.
  const trail = useWalkedTrail();
  const walked = trail.length ? <TrailLinks trail={trail} /> : undefined;
  const toast = useToast();
  const { confirm, confirmWithReason } = useConfirm();
  const { groups: callerGroups } = useMyGroups();
  const [detail, setDetail] = useState<StudyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [callerAccountId, setCallerAccountId] = useState<string | null>(null);
  const [callerRole, setCallerRole] = useState<"admin" | "builder" | "viewer">("viewer");
  const [openFinding, setOpenFinding] = useState<{ number: number; body: string; title: string } | null>(null);
  const [openNote, setOpenNote] = useState<{ name: string; title: string; body: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /**
   * ADR-0181 — a same-filename collision inside one series is a real
   * validation error and shows INLINE here, not as a toast (§2.9). The old
   * `immutable_path` toast is gone: re-landing is now a new version, so the
   * only way to hit this is landing a name that already exists.
   */
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [retention, setRetention] = useState<RetentionInfo | null>(null);
  const [expandedSeries, setExpandedSeries] = useState<Set<string>>(new Set());
  const [retentionBusy, setRetentionBusy] = useState(false);
  /** ADR-0181 — the file waiting on the new-document-vs-new-version choice. */
  const [pendingUpload, setPendingUpload] = useState<File | null>(null);
  // ADR-0157 §6 — the captured-queries panel (read-only; paged with "Show more").
  const [captures, setCaptures] = useState<CapturedQuery[]>([]);
  const [captureAccounting, setCaptureAccounting] = useState<CaptureAccounting | null>(null);
  const [captureTotal, setCaptureTotal] = useState(0);
  const [capturesError, setCapturesError] = useState<string | null>(null);
  const [capturesBusy, setCapturesBusy] = useState(false);

  const CAPTURE_PAGE = 50;

  const loadCaptures = useCallback(
    async (offset: number) => {
      setCapturesBusy(true);
      try {
        const r = await apiFetch<{ captures: CapturedQuery[]; total: number; accounting: CaptureAccounting | null }>(
          `/api/tenant/studies/${encodeURIComponent(slug)}/captures?offset=${offset}&limit=${CAPTURE_PAGE}`
        );
        setCaptures((prev) => (offset === 0 ? r.captures : [...prev, ...r.captures]));
        setCaptureTotal(r.total);
        setCaptureAccounting(r.accounting);
        setCapturesError(null);
      } catch (e) {
        setCapturesError(e instanceof Error ? e.message : String(e));
      } finally {
        setCapturesBusy(false);
      }
    },
    [slug]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, me, ret] = await Promise.all([
        apiFetch<StudyDetail>(`/api/tenant/studies/${encodeURIComponent(slug)}`),
        apiFetch<{ user: { id: string; entitlement?: string; role?: string } }>("/api/tenant/me").catch(() => null),
        // ADR-0181: eligibility is computed hub-side by the one owning module.
        // A failure here hides the retention card rather than failing the page —
        // retention is housekeeping, the record is the point.
        apiFetch<RetentionInfo>(`/api/tenant/studies/${encodeURIComponent(slug)}/retention`).catch(() => null),
      ]);
      setDetail(data);
      setRetention(ret);
      if (me) {
        setCallerAccountId(me.user.id);
        const role = me.user.entitlement ?? me.user.role ?? "viewer";
        setCallerRole(role === "admin" || role === "builder" ? role : "viewer");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setCaptures([]);
    void loadCaptures(0);
  }, [loadCaptures]);

  const viewFinding = async (n: number) => {
    if (openFinding?.number === n) {
      setOpenFinding(null);
      return;
    }
    try {
      const r = await apiFetch<{ finding: FindingIndexRow & { body: string } }>(
        `/api/tenant/studies/${encodeURIComponent(slug)}/findings/${n}`
      );
      setOpenFinding({ number: n, body: r.finding.body, title: r.finding.title });
      setOpenNote(null);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  };

  const viewNote = async (name: string) => {
    if (openNote?.name === name) {
      setOpenNote(null);
      return;
    }
    try {
      const r = await apiFetch<{ note: { name: string; title: string; body: string } }>(
        `/api/tenant/studies/${encodeURIComponent(slug)}/notes/${encodeURIComponent(name)}`
      );
      setOpenNote({ name: r.note.name, title: r.note.title, body: r.note.body });
      setOpenFinding(null);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  };

  const [mirrorBusy, setMirrorBusy] = useState(false);

  const mirrorNow = async () => {
    setMirrorBusy(true);
    try {
      await apiFetch(`/api/tenant/studies/${encodeURIComponent(slug)}/mirror-now`, { method: "POST", body: {} });
      toast.push({ kind: "success", message: "Mirror sync ran." });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setMirrorBusy(false);
    }
  };

  const [landingBusy, setLandingBusy] = useState(false);

  /** Open the door's landing window for this study (the gesture the agent cannot make for itself). */
  const approveLanding = async () => {
    setLandingBusy(true);
    try {
      await apiFetch(`/api/tenant/studies/${encodeURIComponent(slug)}/landing-approval`, { method: "POST", body: {} });
      toast.push({ kind: "success", message: "Agent landing approved for 30 minutes." });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setLandingBusy(false);
    }
  };

  const revokeLanding = async () => {
    setLandingBusy(true);
    try {
      await apiFetch(`/api/tenant/studies/${encodeURIComponent(slug)}/landing-approval`, { method: "DELETE" });
      toast.push({ kind: "success", message: "Agent landing closed." });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setLandingBusy(false);
    }
  };

  const remirror = async (path: string, state: MirrorArtifactStatus["state"]) => {
    const ok = await confirm({
      title: `Re-mirror “${path}”?`,
      detail:
        state === "diverged"
          ? "The mirror copy was edited on the Microsoft side. Re-mirroring replaces it with Atlas's original — the edits there are overwritten."
          : "Atlas pushes its original again, replacing whatever is at that path on the mirror.",
      confirmLabel: "Re-mirror"
    });
    if (!ok) return;
    setMirrorBusy(true);
    try {
      await apiFetch(`/api/tenant/studies/${encodeURIComponent(slug)}/documents/remirror`, {
        method: "POST",
        body: { path }
      });
      toast.push({ kind: "success", message: `Re-mirrored ${path}.` });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setMirrorBusy(false);
    }
  };

  const landFile = async (file: File, target: { series?: string; seriesName?: string }) => {
    setUploading(true);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      if (target.series) form.append("series", target.series);
      if (target.seriesName) form.append("series_name", target.seriesName);
      const result = await apiFetchMultipart<{ version_no: number; series_key: string }>(
        `/api/tenant/studies/${encodeURIComponent(slug)}/documents`,
        form
      );
      toast.push({
        kind: "success",
        message:
          result.version_no > 1
            ? `Landed ${file.name} as version ${result.version_no}.`
            : `Landed ${file.name}.`,
      });
      await load();
    } catch (e) {
      // A same-filename collision is a validation error, so it shows inline
      // next to the control that caused it — never as a toast (§2.9).
      if (e instanceof ApiRequestError && e.code === "immutable_path") {
        setUploadError(
          `A file named “${file.name}” is already landed on this study. Rename the file, or land it as a new version of the document it belongs to.`
        );
      } else {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  /**
   * ADR-0181 §3.2 — new document, or new version of an existing one?
   *
   * When the study has NO series yet there is only one possible answer, so the
   * dialog is skipped entirely (UI_GUIDELINES §1.7 — do not ask a question with
   * one answer). Otherwise the user picks, defaulting to a new document.
   */
  const uploadDocument = async (file: File) => {
    setUploadError(null);
    const existing = (detail?.artifacts ?? []).filter((a) => a.kind === "document" && a.series_key);
    if (existing.length === 0) {
      // No series yet ⇒ one possible answer ⇒ no dialog (§1.7). The hub derives
      // the name from the filename, which is the only rule in play here.
      await landFile(file, {});
      return;
    }
    setPendingUpload(file);
  };

  /** ADR-0181 — pin/unpin is non-destructive and reversible: no confirm dialog. */
  const setPinned = async (seriesKey: string, versionNo: number, pinned: boolean) => {
    setRetentionBusy(true);
    try {
      await apiFetch(`/api/tenant/studies/${encodeURIComponent(slug)}/documents/pin`, {
        method: "POST",
        body: { series: seriesKey, version_no: versionNo, pinned },
      });
      toast.push({ kind: "success", message: pinned ? `Pinned version ${versionNo}.` : `Unpinned version ${versionNo}.` });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setRetentionBusy(false);
    }
  };

  /**
   * ADR-0181 §3.3 — purge one series' eligible versions.
   *
   * The detail text states all four consequences, because this is the one
   * irreversible step in the feature and the person confirming it should not
   * have to hold any of them in their head.
   */
  const purgeSeries = async (row: { series_key: string; name: string; eligible_versions: number; reclaims_bytes: number }) => {
    const mirrorTarget = detail?.artifacts.find((a) => a.mirror)?.mirror?.target;
    const reason = await confirmWithReason({
      title: `Purge ${row.eligible_versions} superseded ${row.eligible_versions === 1 ? "version" : "versions"} of “${row.name}”?`,
      detail:
        `The files are deleted and ${formatBytes(row.reclaims_bytes)} is reclaimed. ` +
        "The record of each version — who landed it, when, and its content hash — is kept permanently. " +
        `Copies already on ${mirrorTarget ?? "any connected document library"} are not touched. ` +
        "Pinned versions and the current version are never purged.",
      confirmLabel: "Purge",
      destructive: true,
      reasonField: {
        label: "Reason",
        placeholder: "e.g. superseded working versions, reclaiming space",
      },
    });
    if (reason === null) return;
    setRetentionBusy(true);
    try {
      const result = await apiFetch<{ purged: Array<{ version_no: number }>; reclaimed_bytes: number }>(
        `/api/tenant/studies/${encodeURIComponent(slug)}/documents/purge`,
        { method: "POST", body: { series: row.series_key, reason } }
      );
      toast.push({
        kind: "success",
        message: `Purged ${result.purged.length} ${result.purged.length === 1 ? "version" : "versions"} · ${formatBytes(result.reclaimed_bytes)} reclaimed.`,
      });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setRetentionBusy(false);
    }
  };

  const canEdit =
    detail != null &&
    (callerRole === "admin" ||
      (detail.scope_block.scope === "personal" && detail.scope_block.owner_account_id === callerAccountId) ||
      (detail.scope_block.scope === "team" &&
        callerRole === "builder" &&
        detail.scope_block.owner_group_id != null &&
        callerGroups.some((g) => g.id === detail.scope_block.owner_group_id)) ||
      (detail.scope_block.scope === "shared" && callerRole === "builder"));

  if (error) {
    return (
      <PageLayout title="Study" breadcrumb={walked}>
        <ErrorState detail={error} />
      </PageLayout>
    );
  }
  if (loading || !detail) {
    return (
      <PageLayout title="Study" breadcrumb={walked}>
        <LoadingState />
      </PageLayout>
    );
  }

  const s = detail.study;
  const landedDocs = detail.artifacts.filter((a) => a.kind === "document" && a.content_hash);
  const anyMirrored = landedDocs.some((a) => a.mirror != null);
  /**
   * ADR-0181 — the Version column appears only when this study actually has a
   * document series. On a study of operations and knowledge entries a column of
   * dashes would be noise (§2.2, one datum per column, nothing that isn't
   * scanned per row).
   */
  const anySeries = detail.artifacts.some((a) => a.kind === "document" && a.series_key && a.versions);

  // ADR-0158 §5 — record state, rendered from the server's object only. A
  // healthy record renders nothing at all: no "all clear" chrome.
  const rs = detail.record_state;
  const signal = (code: string): RecordStateSignal | undefined => rs?.signals.find((x) => x.code === code);
  const narrativeStale = signal("narrative_stale");
  const narrativeNotStarted = signal("narrative_not_started");
  const sectionsUnwritten = signal("sections_unwritten");
  const provenanceWarns = signal("provenance_machine_ids");
  const instrumentUnconsumed = signal("instrument_without_consumers");

  /**
   * ADR-0189 — the Consumers table, one row per consuming thing.
   *
   * A row links only where the viewer can actually open the target: a consumer
   * study the server withheld (scope) has no slug, and tenant knowledge entries
   * live behind an admin-only route. In both cases the name still renders — the
   * edge is the point, and a link that lands on a redirect is worse than text.
   */
  const consumption = detail.consumption;
  const edgeWords: Record<string, string> = {
    references_study: "references this study",
    attached_operation: "shares an operation with this study",
    reads_product: "reads its products",
  };
  const consumerRows: Array<{ key: string; name: string; kind: string; via: string; to?: string }> = [];
  for (const op of consumption?.consumers.operations ?? []) {
    const datasets = op.via_datasets.map((d) => d.name).filter(Boolean);
    consumerRows.push({
      key: `op:${op.id}`,
      name: op.name,
      kind: "operation",
      via: datasets.length > 0 ? `reads ${datasets.join(", ")}` : "reads a stored dataset",
      to: `/app/build/operations/${op.id}`,
    });
  }
  for (const page of consumption?.consumers.pages ?? []) {
    consumerRows.push({
      key: `page:${page.id}`,
      name: page.name,
      kind: "page",
      via: `shows ${page.via_operation}`,
      to: `/app/build/pages/${page.id}`,
    });
  }
  for (const study of consumption?.consumers.studies ?? []) {
    consumerRows.push({
      key: `study:${study.study_id}`,
      name: study.title ?? `Study ${study.study_id}`,
      kind: "study",
      via: study.edges.map((e) => edgeWords[e] ?? e).join(" · "),
      ...(study.slug ? { to: `/app/studies/${encodeURIComponent(study.slug)}` } : {}),
    });
  }
  for (const entry of consumption?.consumers.knowledge_entries ?? []) {
    const reads =
      entry.read_count === 0
        ? `never read · offered in ${entry.served_count} ${entry.served_count === 1 ? "search" : "searches"}`
        : `${entry.read_count} ${entry.read_count === 1 ? "read" : "reads"}${
            entry.last_read_at ? ` · last ${formatTimestamp(entry.last_read_at)}` : ""
          }`;
    consumerRows.push({
      key: `knowledge:${entry.ref}`,
      name: entry.ref,
      kind: "knowledge",
      via: reads,
      ...(callerRole === "admin"
        ? { to: `/app/admin/tenant-knowledge/entry/${encodeURIComponent(entry.ref)}` }
        : {}),
    });
  }
  const machineryParts: string[] = [];
  const scheduleNames = (consumption?.machinery.schedules ?? []).map((m) => m.name);
  const watchNames = (consumption?.machinery.watches ?? []).map((m) => m.name);
  if (scheduleNames.length > 0) machineryParts.push(`Maintained by ${scheduleNames.join(", ")}`);
  if (watchNames.length > 0) machineryParts.push(`watches: ${watchNames.join(", ")}`);
  const machineryLine = machineryParts.length > 0 ? machineryParts.join(" · ") : null;
  const unlandedRefs = signal("deliverables_unlanded");
  const staleFixtures = signal("fixtures_stale");
  const overviewLines = (rs?.signals ?? []).filter((x) => OVERVIEW_SIGNALS.includes(x.code));

  return (
    <PageLayout
      breadcrumb={walked}
      title={
        <span className="breadcrumbTitle">
          <Link to="/app/studies" style={{ color: "inherit", textDecoration: "none", opacity: 0.6 }}>
            Studies
          </Link>
          <span style={{ opacity: 0.4, margin: "0 6px" }}>/</span>
          <span>
            <span className="mono">{s.id}</span> — {s.title}
          </span>
        </span>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        <ScopeHeader
          entityKind="study"
          entityId={detail.scope_block.id}
          destructiveConfirmation={{
            value: detail.study.title,
            label: "Type the study's title to confirm:",
            detail:
              "This deletes the study's record, its findings, fixtures and notes, and every landed document — permanently. Archiving keeps all of it and takes the study out of the way instead.",
          }}
          scope={detail.scope_block.scope}
          ownerGroupId={detail.scope_block.owner_group_id}
          ownerGroupName={detail.scope_block.owner_group_name}
          ownerAccountId={detail.scope_block.owner_account_id}
          scopeInheritedFrom={
            detail.scope_inherited_from
              ? {
                  note: `${detail.scope_inherited_from.id} — ${detail.scope_inherited_from.title}`,
                  href: `/app/studies/${encodeURIComponent(detail.scope_inherited_from.slug)}`,
                }
              : null
          }
          callerAccountId={callerAccountId}
          callerRole={callerRole}
          callerGroupIds={callerGroups.map((g) => g.id)}
          onScopeChanged={() => void load()}
          disableClone
        />

        {/* Objective + resume anchor — the machine-owned facts, surfaced first. */}
        <div className="card">
          <div style={{ display: "grid", gap: 10 }}>
            <div className="field">
              <span className="label">Objective</span>
              <div style={{ fontSize: 13 }}>{s.objective}</div>
            </div>
            {s.resume_state ? (
              <div className="field">
                <span className="label">Resume anchor</span>
                <div style={{ fontSize: 13 }}>{s.resume_state}</div>
              </div>
            ) : null}
            <div className="field">
              <span className="label">Type</span>
              <div style={{ fontSize: 13 }}>{s.type ?? <span className="muted">—</span>}</div>
            </div>
            <div className="field">
              <span className="label">Initiatives</span>
              <div style={{ fontSize: 13 }}>
                <InitiativeLinks initiatives={detail.initiatives} />
              </div>
            </div>
            <div className="field">
              <span className="label">Tags</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {s.tags.length === 0 ? <span className="muted">—</span> : s.tags.map((t) => <span key={t} className="pill pillNeutral">{t}</span>)}
              </div>
            </div>
            {s.superseded_by ? (
              <div className="field">
                <span className="label">Superseded by</span>
                <div style={{ fontSize: 13 }}>
                  Study <span className="mono">{s.superseded_by}</span>
                </div>
              </div>
            ) : null}
            <div className="muted" style={{ fontSize: 12 }}>
              Updated {formatTimestamp(s.updated_at)} · Created {formatTimestamp(s.created_at)}
              {s.created_by ? ` by ${s.created_by}` : ""}
            </div>
            {detail.usage ? (
              <div className="muted" style={{ fontSize: 12 }}>{usageProse(detail.usage)}</div>
            ) : null}
            {overviewLines.map((sig) => (
              <div key={sig.code} className="muted" style={{ fontSize: 12 }}>
                {sig.message}
              </div>
            ))}
          </div>
        </div>

        {/* Scope rules — declared pointers to their enforcement homes. */}
        {detail.scope_rules.length > 0 ? (
          <div className="card adminList">
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Scope rules</div>
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Rule</th>
                    <th>Enforced at</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.scope_rules.map((r) => (
                    <tr key={r.statement}>
                      <td>{r.statement}</td>
                      <td>
                        {r.enforced ? (
                          <span className="mono" style={{ fontSize: 12 }}>{r.authority}</span>
                        ) : (
                          <span className="pill pillWarn">Prose only — unenforced</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {/* Narrative body. */}
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{ fontWeight: 600 }}>Narrative</div>
            {narrativeNotStarted
              ? signalPill("Not written")
              : narrativeStale
                ? signalPill(
                    `Stale — ${rs?.counts.findings_since_narrative_update} ${
                      rs?.counts.findings_since_narrative_update === 1 ? "finding" : "findings"
                    } since last update`
                  )
                : null}
          </div>
          {sectionsUnwritten ? (
            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
              {sectionsUnwritten.message}
            </div>
          ) : null}
          <div className="knowledgeMarkdown" style={{ fontSize: 13 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{detail.body}</ReactMarkdown>
          </div>
        </div>

        {/* Findings — compact index (№, title, kind, date); provenance + body live in the row expansion. */}
        <div className="card adminList">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{ fontWeight: 600 }}>Findings</div>
            {provenanceWarns
              ? signalPill(
                  `${rs?.counts.provenance_warns} provenance ${rs?.counts.provenance_warns === 1 ? "warning" : "warnings"}`
                )
              : null}
          </div>
          {detail.findings_index.length === 0 ? (
            <div className="adminListEmpty">No findings logged yet.</div>
          ) : (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Title</th>
                    <th>Kind</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.findings_index.map((f) => (
                    <Fragment key={f.number}>
                      <tr style={f.status === "superseded" ? { opacity: 0.5 } : undefined}>
                        <td className="mono">{f.number}</td>
                        <td style={{ maxWidth: 420 }}>
                          <button
                            className="adminListAction"
                            type="button"
                            title={f.title}
                            aria-expanded={openFinding?.number === f.number}
                            style={{
                              display: "block",
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign: "left",
                            }}
                            onClick={() => void viewFinding(f.number)}
                          >
                            {f.title}
                          </button>
                        </td>
                        <td>
                          {f.kind === "decision" ? <span className="pill">Decision</span> : <span className="pill pillNeutral">Finding</span>}
                          {f.redacted_at ? (
                            <span className="pill pillNeutral" style={{ marginLeft: 6 }}>
                              Redacted
                            </span>
                          ) : null}
                        </td>
                        <td>{f.date}</td>
                      </tr>
                      {openFinding?.number === f.number ? (
                        <tr>
                          <td colSpan={4}>
                            <div style={{ display: "grid", gap: 8, padding: "4px 0" }}>
                              {f.redacted_at ? (
                                <div className="muted" style={{ fontSize: 12 }}>
                                  Redacted {formatTimestamp(f.redacted_at)}
                                  {actorLabel(f.redacted_by) ? ` by ${actorLabel(f.redacted_by)}` : ""}
                                  {f.redaction_reason ? ` — ${f.redaction_reason}` : ""}
                                </div>
                              ) : null}
                              <div className="muted" style={{ fontSize: 12 }}>
                                Provenance: {f.provenance}
                                {f.status === "superseded" ? " · Superseded" : ""}
                                {f.supersedes.length > 0 ? ` · Supersedes ${f.supersedes.join(", ")}` : ""}
                              </div>
                              <div className="knowledgeMarkdown" style={{ fontSize: 13 }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{openFinding.body}</ReactMarkdown>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Artifacts. */}
        <div className="card adminList">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontWeight: 600 }}>Artifacts</div>
              {unlandedRefs
                ? signalPill(
                    `${rs?.counts.unlanded_references} unlanded ${
                      rs?.counts.unlanded_references === 1 ? "reference" : "references"
                    }`
                  )
                : null}
            </div>
            {canEdit ? (
              <div style={{ display: "flex", gap: 8 }}>
                {anyMirrored ? (
                  <button className="btn btnSm" type="button" disabled={mirrorBusy} onClick={() => void mirrorNow()}>
                    {mirrorBusy ? "Syncing…" : "Mirror now"}
                  </button>
                ) : null}
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void uploadDocument(f);
                  }}
                />
                <button className="btn btnSm" type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                  {uploading ? "Uploading…" : "Upload document"}
                </button>
              </div>
            ) : null}
          </div>
          {canEdit ? (
            <div
              className="help"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 }}
            >
              {detail.landing_approval ? (
                <>
                  <span>
                    Agent landing approved by {detail.landing_approval.approved_by} until{" "}
                    {new Date(detail.landing_approval.expires_at).toLocaleString()}
                    {detail.landing_approval.use_count > 0
                      ? ` · used ${detail.landing_approval.use_count.toLocaleString()} ${
                          detail.landing_approval.use_count === 1 ? "time" : "times"
                        }`
                      : ""}
                  </span>
                  <button className="btn btnSm" type="button" disabled={landingBusy} onClick={() => void revokeLanding()}>
                    {landingBusy ? "Closing…" : "Close landing"}
                  </button>
                </>
              ) : (
                <>
                  <span>
                    A connected agent cannot land documents or attach artifacts here until you approve it — the
                    approval stays open for 30 minutes.
                  </span>
                  <button className="btn btnSm" type="button" disabled={landingBusy} onClick={() => void approveLanding()}>
                    {landingBusy ? "Approving…" : "Approve agent landing"}
                  </button>
                </>
              )}
            </div>
          ) : null}
          {uploadError ? (
            <div className="help" style={{ color: "var(--danger-text)", marginBottom: 8 }}>
              {uploadError}
            </div>
          ) : null}
          {detail.artifacts.length === 0 ? (
            <div className="adminListEmpty">No artifacts attached yet.</div>
          ) : (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Kind</th>
                    <th>Reference</th>
                    {anySeries ? <th>Version</th> : null}
                    <th>Note</th>
                    <th>Added</th>
                    {anyMirrored ? <th>Mirror</th> : null}
                    {anyMirrored && canEdit ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {detail.artifacts.flatMap((a, i) => [
                    <tr key={`${a.kind}-${a.ref ?? a.location ?? i}`}>
                      <td>
                        <span className="pill pillNeutral">{a.kind === "knowledge_entry" ? "knowledge" : a.kind}</span>
                      </td>
                      <td>
                        {a.kind === "operation" && a.operation_id && !a.warning ? (
                          <Link className="adminListName" to={`/app/build/operations/${a.operation_id}`}>
                            {a.ref}
                          </Link>
                        ) : a.kind === "document" && a.series_key && a.versions ? (
                          <span>
                            {a.current_version_no != null ? (
                              <a
                                className="adminListName"
                                href={`/api/tenant/studies/${encodeURIComponent(slug)}/documents/${a.ref}`}
                              >
                                {a.name ?? a.series_key}
                              </a>
                            ) : (
                              // Every version purged: the document is still a
                              // record, so it is named — it just has no bytes.
                              <span className="adminListName">{a.name ?? a.series_key}</span>
                            )}
                            {a.bytes != null && a.current_version_no != null ? (
                              <span className="muted"> ({formatBytes(a.bytes)})</span>
                            ) : null}
                            {(a.version_count ?? 0) > 1 ? (
                              <button
                                type="button"
                                className="disclosureToggle disclosureToggleInline"
                                aria-expanded={expandedSeries.has(a.series_key)}
                                onClick={() =>
                                  setExpandedSeries((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(a.series_key!)) next.delete(a.series_key!);
                                    else next.add(a.series_key!);
                                    return next;
                                  })
                                }
                              >
                                {expandedSeries.has(a.series_key)
                                  ? "Hide versions"
                                  : `Show all ${a.version_count} versions`}
                              </button>
                            ) : null}
                          </span>
                        ) : a.kind === "document" && a.content_hash ? (
                          <span>
                            <a className="adminListName" href={`/api/tenant/studies/${encodeURIComponent(slug)}/documents/${a.ref}`}>
                              {a.ref}
                            </a>
                            {a.bytes != null ? <span className="muted"> ({formatBytes(a.bytes)})</span> : null}
                          </span>
                        ) : a.kind === "knowledge_entry" ? (
                          <span className="mono" style={{ fontSize: 12 }}>{a.ref}</span>
                        ) : (
                          <span style={{ fontSize: 13 }}>{a.ref ?? a.location}</span>
                        )}
                        {a.warning ? (
                          <div className="help" style={{ color: "var(--danger-text)" }}>
                            {a.warning}
                          </div>
                        ) : null}
                      </td>
                      {anySeries ? (
                        <td>
                          {a.series_key && a.versions ? (
                            <span className="pill pillNeutral">
                              {a.current_version_no == null
                                ? `${a.version_count} purged`
                                : (a.version_count ?? 1) > 1
                                  ? `v${a.current_version_no} of ${a.version_count}`
                                  : `v${a.current_version_no}`}
                            </span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      ) : null}
                      <td className="muted">{a.note ?? "—"}</td>
                      <td>{formatTimestamp(a.added_at)}</td>
                      {anyMirrored ? (
                        <td>
                          {a.mirror ? (
                            <span
                              className={mirrorPill(a.mirror.state).className}
                              title={`${a.mirror.target}${
                                a.mirror.last_verified_at
                                  ? ` · verified ${formatTimestamp(a.mirror.last_verified_at)}`
                                  : ""
                              }`}
                            >
                              {mirrorPill(a.mirror.state).label}
                            </span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      ) : null}
                      {anyMirrored && canEdit ? (
                        <td>
                          {a.mirror && a.ref && canRemirror(a.mirror.state) ? (
                            <button
                              className="adminListAction"
                              type="button"
                              disabled={mirrorBusy}
                              onClick={() => void remirror(a.ref!, a.mirror!.state)}
                            >
                              Re-mirror
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>,
                    /* ADR-0181 §3.1 — the version chain, newest first. Rendered
                       as a sibling row rather than nested inside the cell so the
                       artifacts table's own column widths are unaffected. */
                    a.series_key && a.versions && expandedSeries.has(a.series_key) ? (
                      <tr key={`${a.series_key}-versions`}>
                        <td colSpan={4 + (anySeries ? 1 : 0) + (anyMirrored ? 1 : 0) + (anyMirrored && canEdit ? 1 : 0)}>
                          <table className="table versionTable">
                            <thead>
                              <tr>
                                <th>Version</th>
                                <th>File</th>
                                <th>Size</th>
                                <th>Landed</th>
                                <th>By</th>
                                <th>State</th>
                                {canEdit ? <th /> : null}
                              </tr>
                            </thead>
                            <tbody>
                              {a.versions.map((v) => {
                                const purged = v.purged_at != null;
                                const isCurrent = a.current_version_no === v.version_no;
                                return (
                                  <tr key={v.version_no}>
                                    <td>v{v.version_no}</td>
                                    <td>
                                      {purged ? (
                                        // No link: the bytes are gone. The
                                        // filename stays — struck through, so
                                        // it cannot be mistaken for something
                                        // clickable — because the record of
                                        // what was here is the point.
                                        <span className="versionFilePurged">{v.path}</span>
                                      ) : (
                                        <a
                                          href={`/api/tenant/studies/${encodeURIComponent(slug)}/documents/${v.path}`}
                                        >
                                          {v.path}
                                        </a>
                                      )}
                                    </td>
                                    <td className="muted">{formatBytes(v.bytes)}</td>
                                    <td>{formatTimestamp(v.landed_at)}</td>
                                    <td className="muted" title={landerLabel(v.landed_by).title}>
                                      {landerLabel(v.landed_by).text}
                                    </td>
                                    <td>
                                      {/* Colour never carries the meaning alone —
                                          every pill is labelled (§6). */}
                                      {purged ? (
                                        <span className="pill pillWarn" title={v.purge_reason ?? undefined}>
                                          Purged
                                        </span>
                                      ) : isCurrent ? (
                                        <span className="pill pillOk">Current</span>
                                      ) : v.pinned ? (
                                        <span className="pill pillOk">Pinned</span>
                                      ) : (
                                        <span className="pill pillNeutral">Superseded</span>
                                      )}
                                    </td>
                                    {canEdit ? (
                                      <td>
                                        {!purged ? (
                                          <button
                                            className="adminListAction"
                                            type="button"
                                            disabled={retentionBusy}
                                            onClick={() => void setPinned(a.series_key!, v.version_no, !v.pinned)}
                                          >
                                            {v.pinned ? "Unpin" : "Pin"}
                                          </button>
                                        ) : null}
                                      </td>
                                    ) : null}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ) : null,
                  ])}
                </tbody>
              </table>
            </div>
          )}
          {landedDocs.length > 0 ? (
            <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              Every version is kept as immutable evidence. Purging a version removes the file and keeps the
              record of it.
            </div>
          ) : null}
        </div>

        {/* ADR-0189 — who reads this study's products. Machinery (the schedules
            and watches that maintain them) is shown below the table as context:
            it is the study's upkeep, deliberately not counted as consumption. */}
        <div className="card adminList">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{ fontWeight: 600 }}>Consumers</div>
            {consumption ? (
              <span className="muted" style={{ fontSize: 12 }}>
                {consumption.summary.consumer_count}
              </span>
            ) : null}
            {instrumentUnconsumed ? signalPill("No consumers") : null}
          </div>
          {consumption === null ? (
            <div className="muted" style={{ fontSize: 13 }}>Consumption is unavailable right now.</div>
          ) : consumerRows.length === 0 ? (
            <div className="adminListEmpty">
              No operation, page, study, or knowledge read consumes this study&rsquo;s products yet.
            </div>
          ) : (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Consumer</th>
                    <th>Kind</th>
                    <th>Via</th>
                  </tr>
                </thead>
                <tbody>
                  {consumerRows.map((r) => (
                    <tr key={r.key}>
                      <td>
                        {r.to ? (
                          <Link className="adminListName" to={r.to}>
                            {r.name}
                          </Link>
                        ) : (
                          r.name
                        )}
                      </td>
                      <td>
                        <span className="pill pillNeutral">{r.kind}</span>
                      </td>
                      <td>{r.via}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {machineryLine ? (
            <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              {machineryLine}
            </div>
          ) : null}
          {instrumentUnconsumed ? (
            <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              {instrumentUnconsumed.message}
            </div>
          ) : null}
        </div>
        {/* ADR-0181 §3.3 — retention. Rendered ONLY when something is eligible:
            a healthy record shows no "all clear" chrome (the ADR-0158 precedent
            already followed elsewhere on this page). */}
        {canEdit && retention && retention.series.length > 0 ? (
          <div className="card adminList">
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Retention</div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
              {retention.series.reduce((n, r) => n + r.eligible_versions, 0)} superseded{" "}
              {retention.series.reduce((n, r) => n + r.eligible_versions, 0) === 1 ? "version" : "versions"} across{" "}
              {retention.series.length} {retention.series.length === 1 ? "document" : "documents"} ·{" "}
              {formatBytes(retention.series.reduce((n, r) => n + r.reclaims_bytes, 0))} ·{" "}
              {Math.round((retention.tenant_bytes / retention.quota_bytes) * 100)}% of the{" "}
              {formatBytes(retention.quota_bytes)} allowance in use
            </div>
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Versions eligible</th>
                    <th>Reclaims</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {retention.series.map((r) => (
                    <tr key={r.series_key}>
                      <td>{r.name}</td>
                      <td>{r.eligible_versions}</td>
                      <td className="muted">{formatBytes(r.reclaims_bytes)}</td>
                      <td>
                        <div className="adminListActions">
                          <button
                            className="adminListAction isDanger"
                            type="button"
                            disabled={retentionBusy}
                            onClick={() => void purgeSeries(r)}
                          >
                            Purge superseded
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {/* ADR-0157 §6 — Captured queries. Read-only: every query this study
            ran, captured automatically. Curation (promote into the operation,
            discard a dead end) is a door gesture in v1. */}
        <div className="card adminList">
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12 }}>
            <div style={{ fontWeight: 600 }}>Captured queries</div>
            {captureAccounting && captureAccounting.total > 0 ? (
              <div className="muted" style={{ fontSize: 12 }}>
                {captureAccounting.total.toLocaleString()} captured · {captureAccounting.promoted.toLocaleString()} promoted ·{" "}
                {captureAccounting.discarded.toLocaleString()} discarded · {captureAccounting.uncurated.toLocaleString()} awaiting
                curation
                {captureAccounting.operation ? ` · ${captureAccounting.operation}` : ""}
              </div>
            ) : null}
          </div>
          {capturesError ? (
            <ErrorState detail={capturesError} />
          ) : capturesBusy && captures.length === 0 ? (
            <LoadingState />
          ) : captures.length === 0 ? (
            <div className="adminListEmpty">
              No queries captured yet. Queries run in this study&rsquo;s sessions are captured automatically.
            </div>
          ) : (
            <>
              <div className="tableWrap">
                <table className="table tableCompact">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Kind</th>
                      <th>Source</th>
                      <th>Outcome</th>
                      <th>Status</th>
                      <th>SQL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {captures.map((c) => (
                      <tr key={c.id}>
                        <td>{formatTimestamp(c.captured_at)}</td>
                        <td>{c.kind === "analysis" ? "Analysis" : "Query"}</td>
                        <td>{c.connector_name ?? "—"}</td>
                        <td>{captureOutcome(c)}</td>
                        <td>
                          {c.disposition === "promoted" ? (
                            <span>
                              Promoted
                              {c.promoted_operation_name ? (
                                <>
                                  {" → "}
                                  <span>{c.promoted_node_label ?? c.promoted_operation_name}</span>
                                </>
                              ) : null}
                            </span>
                          ) : c.disposition === "discarded" ? (
                            <span className="muted" title={c.discard_reason ?? undefined}>
                              Discarded
                            </span>
                          ) : (
                            <span>Awaiting curation</span>
                          )}
                        </td>
                        {c.redacted_at ? (
                          // The SQL is gone from the cell AND from the tooltip.
                          // Leaving `title` pointed at c.sql would have kept
                          // serving the removed text on hover.
                          <td title={c.redaction_reason ?? undefined}>
                            <span className="pill pillNeutral">Redacted</span>
                            {c.redaction_reason ? (
                              <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>
                                {c.redaction_reason}
                              </span>
                            ) : null}
                          </td>
                        ) : (
                          <td
                            className="mono"
                            style={{ fontSize: 12, maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={c.sql}
                          >
                            {c.sql}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {captures.length < captureTotal ? (
                <div className="actions" style={{ marginTop: 8 }}>
                  <button className="btn btnSm" type="button" disabled={capturesBusy} onClick={() => void loadCaptures(captures.length)}>
                    {capturesBusy ? "Loading…" : "Show more"}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Fixtures. */}
        <div className="card adminList">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{ fontWeight: 600 }}>Fixtures</div>
            {staleFixtures ? signalPill(`${rs?.counts.stale_fixtures} stale`) : null}
          </div>
          {detail.fixtures.length === 0 ? (
            <div className="adminListEmpty">No ground-truth fixtures recorded yet.</div>
          ) : (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Operation</th>
                    <th>Output</th>
                    <th>Last check</th>
                    <th>Checked</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.fixtures.map((f) => (
                    <tr key={f.name}>
                      <td className="mono">{f.name}</td>
                      <td>{f.operation}</td>
                      <td>{f.output}</td>
                      <td>
                        {checkPill(f)}
                        {f.stale ? <span className="pill pillWarn" style={{ marginLeft: 6 }}>Stale</span> : null}
                      </td>
                      <td>{formatTimestamp(f.last_check?.checked_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Notes. */}
        <div className="card adminList">
          <div style={{ fontWeight: 600, marginBottom: 12 }}>Notes</div>
          {detail.notes.length === 0 ? (
            <div className="adminListEmpty">No notes yet.</div>
          ) : (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Title</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.notes.map((n) => (
                    <tr key={n.name}>
                      <td className="mono">{n.name}</td>
                      <td>
                        <button
                          className="adminListAction"
                          type="button"
                          aria-expanded={openNote?.name === n.name}
                          onClick={() => void viewNote(n.name)}
                        >
                          {n.title}
                        </button>
                      </td>
                      <td>{formatTimestamp(n.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {openNote ? (
            <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{openNote.title}</div>
              <div className="knowledgeMarkdown" style={{ fontSize: 13 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{openNote.body}</ReactMarkdown>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {pendingUpload ? (
        <LandDocumentDialog
          filename={pendingUpload.name}
          series={detail.artifacts
            .filter((a) => a.kind === "document" && a.series_key)
            .map((a) => ({ key: a.series_key!, name: a.name ?? a.series_key! }))}
          onCancel={() => {
            setPendingUpload(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
          onLand={(target: LandTarget) => {
            const file = pendingUpload;
            setPendingUpload(null);
            void landFile(file, target);
          }}
        />
      ) : null}
    </PageLayout>
  );
}
